import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";

const state = {
    instanceWorkgroupDetail: null,
    locationSkills: [],
    instanceWorkgroupTeams: [],
    scheduledPersons: [],
    instanceWorkgroupAdmins: [],
    loading: false,
    error: null,
    saveResponse: null,
    saveScheduledPersonsResponse: null,
    saveAdminsResponse: null,
};

const mutations = {
    SET_INSTANCE_WORKGROUP_DETAIL(state, detail) {
        state.instanceWorkgroupDetail = detail;
    },
    SET_LOCATION_SKILLS(state, skills) {
        state.locationSkills = skills;
    },
    SET_INSTANCE_WORKGROUP_TEAMS(state, teams) {
        state.instanceWorkgroupTeams = teams;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SAVE_RESPONSE(state, response) {
        state.saveResponse = response;
    },
    SET_SCHEDULED_PERSONS(state, persons) {
        state.scheduledPersons = persons;
    },
    SET_SAVE_SCHEDULED_PERSONS_RESPONSE(state, response) {
        state.saveScheduledPersonsResponse = response;
    },
    SET_INSTANCE_WORKGROUP_ADMINS(state, admins) {
        state.instanceWorkgroupAdmins = admins;
    },
    SET_SAVE_ADMINS_RESPONSE(state, response) {
        state.saveAdminsResponse = response;
    },
};

const actions = {
    /**
     * ✅ Get instance workgroup details
     * API: GET /manageinstanceworkgroup/details?instanceworkgroupid={id}
     */
    async getInstanceWorkgroupDetails({ commit }, instanceWorkgroupId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.get("/manageinstanceworkgroup/details", {
                params: {
                    instanceworkgroupid: instanceWorkgroupId,
                },
            });

            const detail = response.data?.data || response.data?.output || response.data;
            commit("SET_INSTANCE_WORKGROUP_DETAIL", detail);
            return detail;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instance workgroup details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get skills by location
     * API: GET /skills/location/{locationId}?all=true
     */
    async getLocationSkills({ commit }, { locationId, all = true }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.get(`/skills/location/${locationId}`, {
                params: {
                    all: all,
                },
            });

            // Skills API returns direct array or wrapped in data
            const skills = response.data?.data?.output || [];
            commit("SET_LOCATION_SKILLS", skills);
            return skills;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch location skills");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get teams for an instance workgroup
     * API: GET /manageinstanceworkgroup/{ai_workgroup_id}/teams
     */
    async getInstanceWorkgroupTeams({ commit }, aiWorkgroupId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.get(`/manageinstanceworkgroup/${aiWorkgroupId}/teams`);

            // Teams API returns { teams: [...] }
            const teams = response.data;
            commit("SET_INSTANCE_WORKGROUP_TEAMS", teams);
            return teams;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instance workgroup teams");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Update instance workgroup details
     * API: POST /manageinstanceworkgroup/details
     */
    async updateInstanceWorkgroupDetails({ commit }, {
        instanceworkgroupid,
        name,
        description,
        person_count,
        sign_up_only,
        skill_id,
        workgroup_id,
        team_id
    }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageinstanceworkgroup/details", {
                instanceworkgroupid,
                name,
                description,
                person_count,
                sign_up_only,
                skill_id,
                workgroup_id,
                team_id,
            });

            const result = response.data?.data || response.data?.output || response.data;
            commit("SET_SAVE_RESPONSE", result);
            commit("SET_INSTANCE_WORKGROUP_DETAIL", result);
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to update instance workgroup details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get scheduled persons for a workgroup instance
     * API: POST /managescheduleajax/
     * cmd: "getscheduledpersons"
     */
    async getScheduledPersons({ commit }, aworkgroupinstanceid) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax/", {
                cmd: "getscheduledpersons",
                aworkgroupinstanceid: aworkgroupinstanceid,
            });

            // Response structure: { all_users_with_skill: [...], scheduled_volunteers: [...], ... }
            const persons = response.data?.output || response.data?.data || response.data || {};
            commit("SET_SCHEDULED_PERSONS", persons);
            return persons;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch scheduled persons");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save scheduled persons for a workgroup instance
     * API: POST /managescheduleajax/
     * cmd: "savescheduledpersons"
     */
    async saveScheduledPersons({ commit }, { aworkgroupinstanceid, userIdsArray }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/managescheduleajax/", {
                cmd: "savescheduledpersons",
                aworkgroupinstanceid: aworkgroupinstanceid,
                useridsarray: userIdsArray,
            });

            const result = response.data?.data || response.data?.output || response.data;
            commit("SET_SAVE_SCHEDULED_PERSONS_RESPONSE", result);
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to save scheduled persons");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Get instance workgroup admins
     * API: GET /manageinstanceworkgroup/admins?instanceworkgroupid={id}
     */
    async getInstanceWorkgroupAdmins({ commit }, instanceWorkgroupId) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.get("/manageinstanceworkgroup/admins", {
                params: {
                    instanceworkgroupid: instanceWorkgroupId,
                },
            });

            const admins = response.data?.data || response.data?.output || response.data || [];
            commit("SET_INSTANCE_WORKGROUP_ADMINS", admins);
            return admins;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch instance workgroup admins");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * ✅ Save instance workgroup admins
     * API: POST /manageinstanceworkgroupajax
     * cmd: "saveadmins"
     */
    async saveInstanceWorkgroupAdmins({ commit }, { workgroupId, userIdsArray }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const response = await api.post("/manageinstanceworkgroupajax", {
                cmd: "saveadmins",
                workgroupid: workgroupId,
                useridsarray: userIdsArray,
            });

            const result = response.data?.data || response.data?.output || response.data;
            commit("SET_SAVE_ADMINS_RESPONSE", result);
            return result;
        } catch (error) {
            const message = handleApiError(error, "Failed to save instance workgroup admins");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    instanceWorkgroupDetail: (state) => state.instanceWorkgroupDetail,
    locationSkills: (state) => state.locationSkills,
    instanceWorkgroupTeams: (state) => state.instanceWorkgroupTeams,
    scheduledPersons: (state) => state.scheduledPersons,
    instanceWorkgroupAdmins: (state) => state.instanceWorkgroupAdmins,
    instanceWorkgroupLoading: (state) => state.loading,
    instanceWorkgroupError: (state) => state.error,
    saveResponse: (state) => state.saveResponse,
    saveScheduledPersonsResponse: (state) => state.saveScheduledPersonsResponse,
    saveAdminsResponse: (state) => state.saveAdminsResponse,
};

export default {
    state,
    mutations,
    actions,
    getters,
};

